import { ref, computed, watch, type ComputedRef } from 'vue'
import { fetchPageBySlug } from '@streampac.io/chef_sp_1'
import { useVideoAsset } from './useVideoAsset'
import { useAuthenticationData } from './useAuthenticationData'
import { type IHomeData, type IRelatedContentsExpanded } from '@/types/HomeData'
import { type IFAQData } from '@/types/FAQData'

// Assuming fetchPageBySlug returns a Promise with a structure that matches IHomeData or IFAQData
const pageData = ref<IHomeData | IFAQData | null>(null)

const isAuthenticated = computed(() => useAuthenticationData().data.value.isAuthenticated)

export interface UseSlugDataModule {
  slugData: ComputedRef<IHomeData | IFAQData | null>
  getData: () => Promise<void>
}

export const useSlugData: (endpoint: string) => UseSlugDataModule = (endpoint: string) => {
  pageData.value = null

  const getData = async () => {
    try {
      if (endpoint) {
        const response = await fetchPageBySlug(isAuthenticated.value, endpoint)
        if (response?.status === 404 || response?.status === 401) {
          throw new Error('Content not found')
        }
        pageData.value = response
      }
    } catch (error) {
      console.error('Error fetching page data:', error)
      pageData.value = null
      throw error // Re-throw the error so the router can handle it
    }
  }

  const slugData = computed(() => pageData.value)

  const allExpandedElements = computed(
    () => slugData.value?.sectionsExpanded?.map((section) => section.elements).flat() || []
  )

  const playlists = computed(() =>
    allExpandedElements.value.reduce((acc: IRelatedContentsExpanded[], elem) => {
      if (elem.entityType === 'Playlist' && elem.playList?.relatedContents?.length > 0) {
        acc.push(elem?.playList?.relatedContentsExpanded)
      }
      return acc
    }, [])
  )

  const mediaIdCollection = computed(() =>
    playlists.value
      .flat()
      .map((content) => content.videoContent?.mediaId)
      .filter((item) => item)
  )

  watch(mediaIdCollection, (newVal, oldVal) => {
    if (newVal !== oldVal && newVal.length > 0) {
      try {
        useVideoAsset().getVideoAsset(newVal)
      } catch (error) {
        console.error('Error in mediaIdCollection watcher:', error)
      }
    }
  })

  return {
    slugData,
    getData
  }
}
